import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { eq } from 'tio-ui/utilities';
import Modal from './modal.gts';
import Button from './button.gts';
import AlertBanner from './alert-banner.gts';
import { getConfirmationTokens } from '../color-tokens.ts';
import type { ConfirmationIntent } from '../color-tokens.ts';
import { PhWarning, PhTrash, PhCheckCircle, PhInfo } from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// ConfirmationDialog – premium confirmation overlay with micro-interactions
// ---------------------------------------------------------------------------

export interface ConfirmationDialogSignature {
  Args: {
    /** Controls whether the dialog is visible */
    isOpen: boolean;
    /** The title displayed in the dialog header */
    title: string;
    /** Visual intent of the dialog
     * - 'warning': Amber styling with warning icon
     * - 'danger': Red styling with trash icon
     * - 'success': Emerald styling with check icon
     * - 'info': Blue styling with info icon
     * @default 'warning'
     */
    intent?: ConfirmationIntent;
    /** Text for the confirm button @default 'Confirm' */
    confirmText?: string;
    /** Text for the cancel button @default 'Cancel' */
    cancelText?: string;
    /** User must type this exact text to enable confirm button */
    typeToConfirm?: string;
    /** Hint text for type-to-confirm input */
    typeToConfirmHint?: string;
    /** Shows loading state on confirm button */
    isRunning?: boolean;
    /** Error message to display (triggers shake animation) */
    error?: string | null;
    /** Called when the user confirms */
    onConfirm: () => void | Promise<void>;
    /** Called when the user cancels or closes */
    onCancel: () => void;
  };
  Blocks: {
    /** Main content of the dialog body */
    default: [];
    /** Optional warning message above main content */
    warning?: [];
  };
  Element: HTMLDivElement;
}

/**
 * ConfirmationDialog — a polished confirmation overlay with:
 * - Glassmorphism gradient header per intent
 * - Animated icon per intent (warning, trash, check, info)
 * - Optional type-to-confirm with intent-colored glow ring
 * - Shake animation on error
 * - Loading state with reimagined Button's three-dot animation
 *
 * @example Basic warning
 * ```gts
 * import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
 *
 * <ConfirmationDialog
 *   @isOpen={{this.showConfirm}}
 *   @title="Archive Application"
 *   @intent="warning"
 *   @onConfirm={{this.handleArchive}}
 *   @onCancel={{this.closeConfirm}}
 * >
 *   This will archive the application. You can restore it later.
 * </ConfirmationDialog>
 * ```
 *
 * @example Danger with type-to-confirm
 * ```gts
 * <ConfirmationDialog
 *   @isOpen={{this.showDelete}}
 *   @title="Delete Record"
 *   @intent="danger"
 *   @typeToConfirm="DELETE"
 *   @onConfirm={{this.handleDelete}}
 *   @onCancel={{this.closeDelete}}
 * >
 *   This action cannot be undone.
 * </ConfirmationDialog>
 * ```
 */
export default class ConfirmationDialog extends Component<ConfirmationDialogSignature> {
  @tracked confirmationInput = '';
  @tracked private _shaking = false;

  /**
   * Modifier that watches @error — when it changes to a new truthy value,
   * triggers the shake animation for 500ms.
   */
  watchError = modifier((_element: HTMLElement, [error]: [string | null | undefined]) => {
    if (error) {
      this._shaking = true;
      const timer = setTimeout(() => {
        this._shaking = false;
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  });

  get intent(): ConfirmationIntent {
    return this.args.intent ?? 'warning';
  }

  get confirmText() {
    return this.args.confirmText ?? 'Confirm';
  }

  get cancelText() {
    return this.args.cancelText ?? 'Cancel';
  }

  get tokens() {
    return getConfirmationTokens(this.intent);
  }

  get typeToConfirmHint() {
    if (this.args.typeToConfirmHint) {
      return this.args.typeToConfirmHint;
    }
    return `Type "${this.args.typeToConfirm}" to confirm`;
  }

  get isConfirmDisabled() {
    if (this.args.isRunning) return true;
    if (this.args.typeToConfirm) {
      return this.confirmationInput.trim() !== this.args.typeToConfirm;
    }
    return false;
  }

  handleInputChange = (event: Event) => {
    this.confirmationInput = (event.target as HTMLInputElement).value;
  };

  handleConfirm = () => {
    this.confirmationInput = '';
    this.args.onConfirm();
  };

  handleCancel = () => {
    this.confirmationInput = '';
    this.args.onCancel();
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleCancel}}
      @size="md"
      @closeOnEscapeKey={{true}}
      @allowCloseButton={{false}}
      data-test-confirmation-dialog
      ...attributes
    >
      {{! Glassmorphism header with intent gradient }}
      <div
        data-test-confirmation-header
        class="px-6 py-5 rounded-t-2xl border-b
          {{this.tokens.headerGradient}}
          {{this.tokens.headerBorder}}"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center bg-white/60 dark:bg-gray-900/40 backdrop-blur-sm shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            data-test-confirmation-icon
          >
            {{#if (eq this.intent "danger")}}
              <PhTrash @size={{22}} class={{this.tokens.iconColor}} />
            {{else if (eq this.intent "success")}}
              <PhCheckCircle @size={{22}} class={{this.tokens.iconColor}} />
            {{else if (eq this.intent "info")}}
              <PhInfo @size={{22}} class={{this.tokens.iconColor}} />
            {{else}}
              <PhWarning @size={{22}} class={{this.tokens.iconColor}} />
            {{/if}}
          </div>
          <h3 data-test-confirmation-title class="text-lg font-semibold text-gray-900">
            {{@title}}
          </h3>
        </div>
      </div>

      {{! Body with optional shake animation }}
      <div
        data-test-confirmation-body
        class="px-6 py-5 space-y-4 {{if this._shaking 'reimagined-shake'}}"
        {{this.watchError @error}}
      >
        {{#if (has-block "warning")}}
          <AlertBanner @intent="warning">
            {{yield to="warning"}}
          </AlertBanner>
        {{/if}}

        <div class="text-gray-700">
          {{yield}}
        </div>

        {{#if @typeToConfirm}}
          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700">
              <span class="mb-1.5 block">{{this.typeToConfirmHint}}</span>
              <input
                type="text"
                value={{this.confirmationInput}}
                {{on "input" this.handleInputChange}}
                placeholder={{@typeToConfirm}}
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-mono bg-white text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow duration-200
                  {{this.tokens.glowRing}}"
                data-test-confirmation-input
              />
            </label>
          </div>
        {{/if}}

        {{#if @error}}
          <AlertBanner @intent="warning" data-test-confirmation-error>
            {{@error}}
          </AlertBanner>
        {{/if}}
      </div>

      {{! Footer with reimagined buttons }}
      <div
        data-test-confirmation-footer
        class="px-6 py-4 border-t border-gray-200 rounded-b-2xl flex justify-end gap-3"
      >
        <Button
          @appearance="outlined"
          @intent="default"
          @isDisabled={{@isRunning}}
          data-test-confirmation-cancel
          {{on "click" this.handleCancel}}
        >
          {{this.cancelText}}
        </Button>
        <Button
          @intent={{this.tokens.buttonIntent}}
          @isRunning={{@isRunning}}
          @isDisabled={{this.isConfirmDisabled}}
          data-test-confirmation-confirm
          {{on "click" this.handleConfirm}}
        >
          {{this.confirmText}}
        </Button>
      </div>
    </Modal>
  </template>
}
